import React from 'react'

interface TermsOfServiceModalProps {
  isOpen: boolean
  onClose: () => void
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Website Terms of Use</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="border-b border-blue-200 mb-6"></div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> September 22, 2025
            </p>

            <p className="text-gray-700 mb-6">
              These Website Terms of Use ("Terms") govern your access to and use of the corporate website operated by Eceptionist ("Company," "we," "our," or "us"). Please read them carefully. By accessing or using our website, you agree to be bound by these Terms.
            </p>

            <p className="text-gray-700 mb-6">
              If you are a paying customer of our SaaS products or services, your relationship with us is governed by a separate Master Services Agreement (MSA) or other written contract. These Terms do not replace or modify any MSA.
            </p>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Scope of Terms</h3>
              <p className="text-gray-700 mb-4">
                These Terms apply only to your use of our corporate website (including all pages, blogs, resources, and downloadable content). They do not apply to the use of our SaaS platform or any subscription services, which are governed by separate agreements.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Eligibility</h3>
              <p className="text-gray-700 mb-4">
                You may use our website only if you are at least 16 years old or the age of majority in your jurisdiction, whichever is greater.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Intellectual Property</h3>
              <p className="text-gray-700 mb-4">
                All content on this website is owned by or licensed to Eceptionist and protected under intellectual property laws worldwide. You may view, download, and print for personal, informational, non-commercial use only.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Acceptable Use</h3>
              <p className="text-gray-700 mb-4">
                You agree not to misuse the site, including no unlawful activities, no automated scraping, no unauthorized access, or misrepresentation.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Privacy and Data Protection</h3>
              <p className="text-gray-700 mb-4">
                Our collection and use of personal data is governed by our Privacy Policy, which complies with GDPR, UK GDPR, CCPA, and other international standards.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">6. Links to Third-Party Sites</h3>
              <p className="text-gray-700 mb-4">
                Our website may contain links to third-party websites. We do not control or endorse those sites.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">7. Disclaimers</h3>
              <p className="text-gray-700 mb-4">
                The website is provided "AS IS" without warranties. Content is informational only.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">8. Limitation of Liability</h3>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by law, Eceptionist shall not be liable for indirect, incidental, or consequential damages.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">9. International Use</h3>
              <p className="text-gray-700 mb-4">
                You are responsible for complying with local laws when accessing the site.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">10. Governing Law</h3>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of the state of Texas in the United States.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">11. Changes</h3>
              <p className="text-gray-700 mb-4">
                We may update these Terms periodically. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">12. Contact Us</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700 mb-2">
                  <strong>Eceptionist Legal</strong> / <a href="mailto:management@eceptionist.com" className="text-blue-600 underline hover:text-blue-800">management@eceptionist.com</a>
                </p>
                <p className="text-gray-700">
                  Eceptionist - 7000 N. Mopac Expressway, Suite 200 – 1312, Austin, Texas 78731
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfServiceModal
